/**
 * Section Carousel Component
 *
 * Quick Picks = YT Music style list with song art
 * Albums = Horizontal scrolling grid
 */

import { MediaCard } from './MediaCard';
import { usePlayerStore } from '@/stores/player';
import { PlayIcon } from '../icons';

interface SectionCarouselProps {
  title: string;
  items: unknown[];
}

export function SectionCarousel({ title, items }: SectionCarouselProps) {
  const { play } = usePlayerStore();

  if (items.length === 0) return null;

  const isSong = (item: any) => {
    const id = item.id || '';
    return (
      !id.startsWith('MPREb_') &&
      !id.startsWith('OLAK') &&
      !id.startsWith('VL') &&
      !id.startsWith('PL') &&
      !id.startsWith('UC') &&
      !id.startsWith('RDCLAK') &&
      id.length > 0
    );
  };

  const handleItemClick = (item: any) => {
    if (isSong(item)) {
      const songs = items.filter((i: any) => isSong(i));
      play(item, songs as any[]);
    }
  };

  const isQuickPicks = title.toLowerCase().includes('quick');
  const isArtistSection = title.toLowerCase().includes('artist');

  // YT Music-style Quick Picks: 2-column list layout
  if (isQuickPicks) {
    return (
      <section>
        <h2 className="text-xl font-bold mb-4">{title}</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {items.slice(0, 12).map((item: any, index) => {
            const thumbnail = item.thumbnail || item.thumbnails?.[0]?.url || './pwa-192x192.webp';
            const title = item.title || item.name || 'Unknown';
            const subtitle = item.artists?.map((a: any) => a.name).join(', ') || item.subtitle || '';

            return (
              <button
                key={item.id || index}
                className="flex items-center gap-3 p-2 rounded-lg hover:bg-surface-container-high transition-colors text-left group"
                onClick={() => handleItemClick(item)}
              >
                {/* Thumbnail */}
                <div className="relative w-14 h-14 shrink-0 rounded-lg overflow-hidden bg-surface-container">
                  <img
                    src={thumbnail}
                    alt={title}
                    className="w-full h-full object-cover"
                    referrerPolicy="no-referrer"
                    crossOrigin="anonymous"
                    loading="lazy"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = './pwa-192x192.webp';
                    }}
                  />
                  {/* Play overlay */}
                  <div className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <PlayIcon className="w-5 h-5 text-white" />
                  </div>
                </div>

                {/* Text */}
                <div className="flex-1 min-w-0">
                  <p className="font-medium text-sm line-clamp-1">{title}</p>
                  {subtitle && (
                    <p className="text-xs text-on-surface-variant line-clamp-1">{subtitle}</p>
                  )}
                </div>
              </button>
            );
          })}
        </div>
      </section>
    );
  }

  // Grid layout for albums
  return (
    <section>
      <h2 className="text-xl font-bold mb-4">{title}</h2>

      <div className="overflow-x-auto scrollbar-hide -mx-4 px-4">
        <div className="flex gap-4">
          {items.map((item: any, index) => (
            <div key={item.id || index} className="shrink-0 w-36 sm:w-40">
              <MediaCard
                item={item}
                variant="grid"
                isArtist={isArtistSection || item.id?.startsWith('UC')}
                onClick={() => handleItemClick(item)}
              />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
